import { formatTime, parseTime, toDateString } from '@seen/core/dates'
import { Button } from '@seen/ui'
import {
  addMinutes,
  format,
  intervalToDuration,
  isValid,
  parseISO,
  setHours,
  setMinutes,
} from 'date-fns'
import type { JSX } from 'react'
import * as React from 'react'
import type { MonthCalendarProps } from '#src/components/calendar/MonthCalendar.tsx'
import { TimeCombobox } from '#src/components/core/combobox/TimeCombobox.tsx'
import type { SmartDatetimeInputProps } from '#src/components/core/SmartDateTimeInput/SmartDateTimeInput.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { useSyncedState } from '#src/hooks/useSyncedState.ts'
import {
  calculateMinEndTime,
  MINUTES_PER_DAY,
  timeToMinutes,
} from '#src/utils/timeParser.ts'

interface DateTimeRangeInputProps extends Omit<
  SmartDatetimeInputProps,
  'value' | 'onChange'
> {
  start?: string
  end?: string
  onChange?: (values: { start: string; end: string }) => void
  monthCalendarProps?: MonthCalendarProps
  defaultDurationMinutes?: number
  minTime?: SmartDatetimeInputProps['minTime']
  maxTime?: SmartDatetimeInputProps['maxTime']
  stepMinutes?: number
}

function isValidEndTime(
  startTime: string,
  endTime: string,
  maxTime: string
): boolean {
  if (!startTime || !endTime) {
    return false
  }
  const startMinutes = timeToMinutes(startTime)
  const endMinutes = timeToMinutes(endTime)
  const maxMinutes = timeToMinutes(maxTime)
  return (
    endMinutes < MINUTES_PER_DAY &&
    endMinutes > startMinutes &&
    endMinutes <= maxMinutes
  )
}

function minutesToTime(minutes: number): string | null {
  if (minutes >= MINUTES_PER_DAY || minutes < 0) {
    return null
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export const DateTimeRangeInput = React.forwardRef<
  HTMLButtonElement,
  DateTimeRangeInputProps
>(
  (
    {
      start,
      end,
      onChange,
      monthCalendarProps,
      defaultDurationMinutes = 30,
      minTime = '00:00',
      maxTime = '23:59',
      stepMinutes = 15,
      ...rest
    },
    ref
  ): JSX.Element => {
    const [date, setDate] = useSyncedState(start)
    const [startTime, setStartTime] = useSyncedState(
      date ? format(date, 'HH:mm') : ''
    )
    let _endTime = ''
    if (end) {
      const parsed = parseISO(end)
      if (isValid(parsed)) {
        _endTime = format(parsed, 'HH:mm')
      }
    } else if (end === undefined && date) {
      _endTime = format(addMinutes(date, defaultDurationMinutes), 'HH:mm')
    }
    const [endTime, setEndTime] = useSyncedState(_endTime)

    const combineDateAndTime = (baseDate: Date, timeStr: string): Date => {
      const [hours, minutes] = timeStr.split(':').map(Number)
      return setMinutes(setHours(baseDate, hours), minutes)
    }

    const handleStartChange = (newDate: string): void => {
      setDate(newDate)
      if (!newDate) {
        onChange?.({ start: '', end: '' })
        return
      }

      const newTime = startTime || '12:00'
      setStartTime(newTime)

      const newStartMinutes = timeToMinutes(newTime)
      const currentEndMinutes = endTime ? timeToMinutes(endTime) : 0
      const currentStartMinutes = startTime ? timeToMinutes(startTime) : 0

      const hasValidCurrentRange =
        endTime && startTime && currentEndMinutes > currentStartMinutes
      const currentDuration = hasValidCurrentRange
        ? currentEndMinutes - currentStartMinutes
        : defaultDurationMinutes

      const potentialEndMinutes = newStartMinutes + currentDuration
      const potentialEndTime = minutesToTime(potentialEndMinutes)

      let nextEndTime = ''
      if (
        potentialEndTime &&
        isValidEndTime(newTime, potentialEndTime, maxTime)
      ) {
        nextEndTime = potentialEndTime
      }
      setEndTime(nextEndTime)

      const _date = parseISO(newDate)
      onChange?.({
        start: combineDateAndTime(_date, newTime).toISOString(),
        end: nextEndTime
          ? combineDateAndTime(_date, nextEndTime).toISOString()
          : '',
      })
    }

    const handleStartTimeChange = (newTime: string): void => {
      setStartTime(newTime)

      const newStartMinutes = timeToMinutes(newTime)
      const currentEndMinutes = endTime ? timeToMinutes(endTime) : 0

      if (endTime && isValidEndTime(newTime, endTime, maxTime)) {
        if (date) {
          onChange?.({
            start: combineDateAndTime(new Date(date), newTime).toISOString(),
            end: combineDateAndTime(new Date(date), endTime).toISOString(),
          })
        }
        return
      }

      const currentStartMinutes = startTime ? timeToMinutes(startTime) : 0
      const hasValidPriorRange =
        endTime && startTime && currentEndMinutes > currentStartMinutes
      const currentDuration = hasValidPriorRange
        ? currentEndMinutes - currentStartMinutes
        : defaultDurationMinutes

      const potentialEndMinutes = newStartMinutes + currentDuration
      const potentialEndTime = minutesToTime(potentialEndMinutes)

      let nextEndTime = ''
      if (
        potentialEndTime &&
        isValidEndTime(newTime, potentialEndTime, maxTime)
      ) {
        nextEndTime = potentialEndTime
      }
      setEndTime(nextEndTime)

      if (date) {
        onChange?.({
          start: combineDateAndTime(new Date(date), newTime).toISOString(),
          end: nextEndTime
            ? combineDateAndTime(new Date(date), nextEndTime).toISOString()
            : '',
        })
      }
    }

    const minEndTime = React.useMemo(
      () => calculateMinEndTime(startTime, minTime, maxTime),
      [startTime, minTime, maxTime]
    )

    return (
      <div className="flex flex-wrap items-stretch gap-2">
        <DatePicker
          {...rest}
          ref={ref}
          monthCalendarProps={monthCalendarProps}
          value={date ? toDateString(date) : null}
          allowUndefined
          onChange={(v) => handleStartChange(v ?? '')}
        />
        <div className="flex flex-wrap items-stretch gap-1">
          <TimeCombobox
            value={startTime}
            onSelect={handleStartTimeChange}
            searchPlaceholder="Type a time..."
            minTime={minTime}
            maxTime={maxTime}
            stepMinutes={stepMinutes}
          >
            <Button
              variant="outline"
              className="h-auto grow justify-start text-xs"
              disabled={rest.disabled}
            >
              {startTime ? formatTime(parseTime(startTime)) : 'Start time'}
            </Button>
          </TimeCombobox>
          <span className="self-center text-muted-foreground">-</span>
          <TimeCombobox
            value={endTime}
            onSelect={(v) => {
              setEndTime(v)
              if (date) {
                onChange?.({
                  start: combineDateAndTime(
                    new Date(date),
                    startTime
                  ).toISOString(),
                  end: combineDateAndTime(new Date(date), v).toISOString(),
                })
              }
            }}
            searchPlaceholder="Type a time..."
            minTime={minEndTime}
            maxTime={maxTime}
            stepMinutes={stepMinutes}
          >
            <Button
              variant="outline"
              className="h-auto grow justify-start text-xs"
              disabled={rest.disabled}
            >
              {endTime ? (
                <>
                  {formatTime(parseTime(endTime))}
                  {startTime && (
                    <span className="ml-1 text-3xs text-muted-foreground">
                      ({formatDuration(startTime, endTime)})
                    </span>
                  )}
                </>
              ) : (
                'End time'
              )}
            </Button>
          </TimeCombobox>
        </div>
      </div>
    )
  }
)

DateTimeRangeInput.displayName = 'DateTimeRangeInput'

const formatDuration = (startTimeStr: string, endTimeStr: string): string => {
  if (!startTimeStr || !endTimeStr) {
    return ''
  }

  const baseDate = new Date()
  const start = parseTime(startTimeStr, baseDate)
  const end = parseTime(endTimeStr, baseDate)

  if (!isValid(start) || !isValid(end)) {
    return ''
  }

  let adjustedEnd = end
  if (end < start) {
    adjustedEnd = new Date(end.setDate(end.getDate() + 1))
  }

  const duration = intervalToDuration({ start, end: adjustedEnd })

  if (!duration.hours && !duration.minutes) {
    return '0m'
  }

  if (!duration.hours) {
    return `${duration.minutes}m`
  }

  if (!duration.minutes) {
    return `${duration.hours}h`
  }

  return `${duration.hours}h ${duration.minutes}m`
}
